import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Operations → Salesforce → Report Problem.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screen "Report a Problem". One screen, two
 * questions; the date goes to Case.Event_Date__c and the detail box becomes
 * the Case Description.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Salesforce',
    subCategory: 'Report Problem',
    subject: subjectRow('Salesforce problem - case page not loading', { noAccount: true }),
    lookups: [[
      'Office',
      <>Required. The screen asks: <em>{'"Please confirm the Office/Team you are with. If you do not see your Office/Team, please reach out to the Operations Team."'}</em></>,
    ]],
    note: 'There is no Household picker on Salesforce requests.',
  }),
  {
    title: 'Describe the problem',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>Report a Problem Details</strong> screen asks two things: the date of the issue and a
        description. Both are required.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the questions, select <em>I have read, understand, and
        agree to the above information</em> under Acknowledgement and click Next. FSC creates the case and
        opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

const QUESTIONS = [
  {
    q: 'What was the Date of this Issue?',
    when: 'Always',
    note: 'The day it happened, or the day you first noticed it.',
  },
  {
    q: 'Please provide as much detail as possible for the Operations Team.',
    when: 'Always',
    note: 'This becomes the case description. Say what you were doing, which page or record, what you expected and what happened instead, including any error message word for word.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Salesforce'),
  {
    h: "My Office/Team isn't in the Office picker",
    p: ['The screen asks you to reach out to the Operations Team if you do not see your Office/Team.'],
  },
];

export default function SalesforceReportProblem() {
  return (
    <GuidePage
      title="Report a Salesforce Problem"
      icon="bug"
      path="Operations → Salesforce → Report Problem"
      minutes={2}
      intro="How to report a problem with Salesforce to the Prosperity Operations team - what to gather first, the questions you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form has no file upload, so put everything in the description.</Para>
        <Bullets items={[
          <><strong>The date</strong> the problem happened.</>,
          <><strong>Where it happened</strong> - the page, record or list you were on.</>,
          <><strong>What you did, what you expected and what happened</strong>, with any error message copied exactly.</>,
        ]} />
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="operations"
        confirmations={false}
        rows={[]}
        before={
          <Para>
            FSC creates the case, shares it with you and your office, and opens it. The date is recorded on the
            case and your description becomes the case description.
          </Para>
        }
      />

      <Trouble items={TROUBLE} />

      <Footnote>This guide describes the request form only.</Footnote>
    </GuidePage>
  );
}
